import React, { useState, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import moment from "moment"
import { useToast } from "~/utils/Toast"
import { Plus, TrashIcon, Send, Loader2, ArrowLeft } from "lucide-react"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchDropdown from "~/library/CatchDropdown"
import CatchTable from "~/library/CatchTable"
import ConfirmationModal from "~/library/ConfirmationModal"
import ResourceNotes from "~/library/ResourceNotes"
import ButtonGroup from "~/library/UIkit/ButtonGroup"

import PaymentModal from "./PaymentModal"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiSponsorships, apiPayments } from "~/routes"
import {
  getSponsorshipsBackPath,
  getSponsorshipsBackTitle,
  clearSponsorshipsNavigationState,
} from "~/utils/navigationState"
import Sponsorship from "~/types/Sponsorship"
import Payment from "~/types/Payment"
import { formatCurrency } from "~/utils/helpers"
import TasksTab from "~/components/Sponsorship/TasksTab"

type ViewTab = "general" | "tasks"

const SponsorshipView = () => {
  const { venueSlug } = getAppConfig()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()

  const [viewTab, setViewTab] = useState<ViewTab>("general")
  const viewTabOptions = [
    { label: "General", value: "general" as ViewTab },
    { label: "Tasks", value: "tasks" as ViewTab },
  ]

  const [isDeleting, setIsDeleting] = useState(false)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [deletingPayment, setDeletingPayment] = useState<string | null>(null)
  const [sendingInvoice, setSendingInvoice] = useState<string | null>(null)
  const [confirmingInvoice, setConfirmingInvoice] = useState<string | null>(null)

  const endpoint = apiSponsorships.show.path({ venueSlug, id: id! })
  const {
    data: sponsorship,
    refetch,
    isLoading,
  } = useFetchResource<{ sponsorship: Sponsorship }, "sponsorship">(endpoint, "sponsorship", {})

  const renderDetail = (title: string, content: React.ReactNode, size = 6) => {
    return (
      <div className={`col-${size || 6}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
          <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
      </div>
    )
  }

  const deleteSponsorship = () => {
    setIsDeleting(true)
    const req = apiSponsorships.destroy({ data: { venueSlug, id: id! } })
    req
      .then(() => {
        toast("Sponsorship deleted successfully", "success")
        clearSponsorshipsNavigationState()
        navigate("/sponsorship")
      })
      .catch((res) => {
        toast(
          `Error deleting sponsorship. ${res.body?.errors?.join(", ") || "Please try again"}`,
          "danger"
        )
        setIsDeleting(false)
      })
  }

  const deletePayment = () => {
    if (!deletingPayment) return

    const req = apiPayments.destroy({
      venueSlug,
      id: deletingPayment,
      payableId: sponsorship.id,
      payableType: "sponsorship",
    })
    req.then(() => {
      setDeletingPayment(null)
      toast("Payment Removed", "success")
      refetch()
    })
  }

  const sendInvoice = () => {
    if (!confirmingInvoice) return

    setSendingInvoice(confirmingInvoice)
    setConfirmingInvoice(null)

    toast("Invoice sending in progress...", "info")

    const req = apiPayments.sendInvoice({
      venueSlug,
      id: confirmingInvoice,
      payableId: sponsorship.id,
      payableType: "sponsorship",
    })
    req
      .then(() => {
        setSendingInvoice(null)
        toast("Invoice sent successfully", "success")
        refetch()
      })
      .catch((res) => {
        setSendingInvoice(null)
        toast(res.body?.errors?.join(", ") || "Failed to send invoice", "danger")
      })
  }

  const formatPaymentStatus = (status: string, isOverdue: boolean) => {
    if (status === "paid") {
      return <span className="badge badge-lg bg-success">Paid</span>
    } else if (status === "pending") {
      return (
        <span className={`badge badge-lg ${isOverdue ? "bg-danger" : "bg-warning"}`}>
          {isOverdue ? "Overdue" : "Pending"}
        </span>
      )
    } else {
      return <span className="badge badge-lg bg-secondary">Draft</span>
    }
  }

  const canSendInvoice = (payment: Payment) => {
    return payment.type === "Invoice" && payment.status === "draft"
  }

  const renderPaymentActions = useCallback(
    (payment: Payment) => {
      const isSending = sendingInvoice === payment.id
      const isPaidInvoice = payment.type === "Invoice" && (payment.status === "paid" || !!payment.paidOn)
      const isSentInvoice = payment.type === "Invoice" && !!payment.sentOn
      const canDelete =
        !["SavedCardCharge", "InstantPayment"].includes(payment.type) &&
        !isPaidInvoice &&
        !isSentInvoice

      return (
        <div className="d-flex gap-1">
          {isSending ? (
            <Loader2 className="text-primary spinner" size={24} />
          ) : (
            canSendInvoice(payment) && (
              <Send
                className="text-primary cursor-pointer"
                onClick={() => setConfirmingInvoice(payment.id)}
              />
            )
          )}
          {canDelete && (
            <TrashIcon
              className={`text-danger ${isSending ? "opacity-50" : "cursor-pointer"}`}
              onClick={isSending ? undefined : () => setDeletingPayment(payment.id)}
            />
          )}
        </div>
      )
    },
    [sendingInvoice]
  )

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle
        title={
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              onClick={() => navigate(getSponsorshipsBackPath())}
              aria-label={`Go back to ${getSponsorshipsBackTitle()}`}
              title={`Go back to ${getSponsorshipsBackTitle()}`}
            >
              <ArrowLeft />
            </button>
            <span>{sponsorship?.company?.name}</span>
            <div className="ms-3">
              <ButtonGroup
                options={viewTabOptions}
                selectedValue={viewTab}
                onClick={(value) => setViewTab(value as ViewTab)}
              />
            </div>
          </div>
        }
      >
        <CatchDropdown
          label="Actions"
          items={[
            {
              label: "Edit Sponsorship",
              to: `/sponsorship/${id}/edit`,
              props: {
                state: { areaType: sponsorship?.area?.areaType },
              },
            },
            {
              label: "Delete Sponsorship",
              className: "text-danger",
              onClick: () => setIsDeleting(true),
            },
          ]}
        />
      </PageTitle>

      {viewTab === "general" && (
        <div className="row">
          <div className="col-8">
            <CatchCard title="Company Information">
              <div className="row">
                {renderDetail("Company Name", sponsorship?.company?.name, 6)}
                {renderDetail("Contact Person", sponsorship?.company?.contactPerson, 6)}
                {sponsorship?.company?.email &&
                  renderDetail("Email", sponsorship?.company?.email, 6)}
                {sponsorship?.company?.phone &&
                  renderDetail("Phone", sponsorship?.company?.phone, 6)}
              </div>
            </CatchCard>

            <CatchCard title="Contract Information">
              <div className="row">
                {renderDetail("Area", sponsorship?.area?.name, 6)}
                {renderDetail(
                  "Area Type",
                  <span
                    className={`badge badge-lg ${
                      sponsorship?.areaType === "physical" ? "bg-primary" : "bg-info"
                    }`}
                  >
                    {sponsorship?.areaType === "physical" ? "Physical" : "Digital"}
                  </span>,
                  6
                )}
                {renderDetail(
                  "Start Date",
                  sponsorship?.startOn ? moment(sponsorship.startOn).format("MMM Do YYYY") : "N/A",
                  6
                )}
                {renderDetail(
                  "End Date",
                  sponsorship?.endOn ? moment(sponsorship.endOn).format("MMM Do YYYY") : "N/A",
                  6
                )}
                {renderDetail(
                  "Price",
                  sponsorship?.price ? formatCurrency(sponsorship.price) : "N/A",
                  6
                )}
                {sponsorship?.quantity && renderDetail("Quantity", sponsorship.quantity, 6)}
              </div>
            </CatchCard>

            <CatchCard>
              <div className="d-flex align-items-center mb-2">
                <h4 className="card-title mb-0">Payments</h4>
                <button
                  className="btn btn-primary ms-auto"
                  onClick={() => setIsPaymentModalOpen(true)}
                >
                  <Plus className="me-2" />
                  Create Invoice
                </button>
              </div>
              <CatchTable
                data={sponsorship?.payments || []}
                emptyMessage="No Payments"
                columns={[
                  {
                    key: "type",
                    header: "Type",
                  },
                  {
                    key: "createdBy",
                    header: "Created By",
                    formatter: (createdBy: any) => createdBy?.name || createdBy?.email,
                  },
                  {
                    key: "amount",
                    header: "Amount",
                    formatter: (amount) => formatCurrency(amount),
                  },
                  {
                    key: "sentOn",
                    header: "Sent On",
                    formatter: (date) => (date ? moment(date).format("MM/D/YYYY HH:mm") : "—"),
                  },
                  {
                    key: "paidOn",
                    header: "Paid On",
                    formatter: (date) => (date ? moment(date).format("MM/D/YYYY HH:mm") : "—"),
                  },
                  {
                    key: "status",
                    header: "Status",
                    formatter: (status, data: Payment) =>
                      formatPaymentStatus(status, data.isOverdue),
                  },
                  {
                    key: "id",
                    header: "Actions",
                    formatter: (_d, data: Payment) => renderPaymentActions(data),
                  },
                ]}
              />
            </CatchCard>
          </div>

          <div className="col-4">
            <CatchCard title="Internal Details">
              <div className="row">
                {sponsorship?.createdAt &&
                  renderDetail(
                    "Created on",
                    moment(sponsorship.createdAt).format("MMM Do YYYY hh:mma"),
                    12
                  )}
                {sponsorship?.updatedAt &&
                  renderDetail(
                    "Updated on",
                    moment(sponsorship.updatedAt).format("MMM Do YYYY hh:mma"),
                    12
                  )}
              </div>
            </CatchCard>

            <CatchCard title="Notes">
              <ResourceNotes resourceType="sponsorship" resourceId={sponsorship?.id} />
            </CatchCard>
          </div>
        </div>
      )}

      {viewTab === "tasks" && <TasksTab />}

      <ConfirmationModal
        isOpen={isDeleting}
        onClose={() => setIsDeleting(false)}
        onConfirm={deleteSponsorship}
        title={`Delete Sponsorship"?`}
        description="This action cannot be undone. Are you sure you want to delete this sponsorship?"
      />

      <ConfirmationModal
        isOpen={!!deletingPayment}
        onClose={() => setDeletingPayment(null)}
        onConfirm={deletePayment}
        title="Delete Payment?"
      />

      <ConfirmationModal
        isOpen={!!confirmingInvoice}
        onClose={() => setConfirmingInvoice(null)}
        onConfirm={sendInvoice}
        title="Send Invoice?"
        description="Are you sure you want to send this invoice? This will email the invoice to the company."
      />

      {isPaymentModalOpen && (
        <PaymentModal
          onClose={() => setIsPaymentModalOpen(false)}
          onSuccess={refetch}
          sponsorshipId={sponsorship?.id}
          sponsorship={sponsorship}
        />
      )}
    </LoadingWrapper>
  )
}

export default SponsorshipView
